import React from 'react';
import { Modal, Form, Input, message } from 'antd';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  CREATE_AB_ADDRESS_MAPPING,
  CreateAbAddressMappingResponse,
} from '../graphql/abAddressGraphql';
import { translateGraphQLError } from '../../../../utils/graphqlUtils';

const { TextArea } = Input;

interface AbAddressCreateModalProps {
  open: boolean;
  hubId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface AbAddressFormValues {
  aFullAddress: string;
  bFullAddress: string;
  reason: string;
}

const AbAddressCreateModal: React.FC<AbAddressCreateModalProps> = ({
  open,
  hubId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<AbAddressFormValues>();

  const [createMapping, { loading }] = useMutation<CreateAbAddressMappingResponse>(
    CREATE_AB_ADDRESS_MAPPING,
    {
      onCompleted: () => {
        message.success(t('wms.abAddress.messages.createSuccess') as string);
        form.resetFields();
        onSuccess();
      },
      onError: (err) => message.error(translateGraphQLError(err, 'errors.system.unknownError')),
    }
  );

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await createMapping({
        variables: {
          input: {
            hubId,
            aFullAddress: values.aFullAddress.trim(),
            bFullAddress: values.bFullAddress.trim(),
            reason: values.reason,
          },
        },
      });
    } catch {

    }
  };

  return (
    <Modal
      title={t('wms.abAddress.createTitle')}
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={t('wms.abAddress.submitReview')}
      cancelText={t('components.button.cancel')}
      width={600}
      destroyOnHidden
      maskClosable={false}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
        labelAlign="right"
        preserve={false}
      >
        <Form.Item
          label={t('wms.abAddress.form.aAddress')}
          name="aFullAddress"
          rules={[{ required: true, whitespace: true, message: t('wms.abAddress.form.aAddressRequired') as string }]}
        >
          <Input
            maxLength={500}
            placeholder={t('wms.abAddress.form.aAddressPlaceholder') as string}
          />
        </Form.Item>

        <Form.Item
          label={t('wms.abAddress.form.bAddress')}
          name="bFullAddress"
          rules={[{ required: true, whitespace: true, message: t('wms.abAddress.form.bAddressRequired') as string }]}
        >
          <Input
            maxLength={500}
            placeholder={t('wms.abAddress.form.bAddressPlaceholder') as string}
          />
        </Form.Item>

        <Form.Item
          label={t('wms.abAddress.form.reason')}
          name="reason"
          rules={[{ required: true, message: t('wms.abAddress.form.reasonRequired') as string }]}
        >
          <TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder={t('wms.abAddress.form.reasonPlaceholder') as string}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AbAddressCreateModal;
